import { Heart, Gift, Users, Calendar, ArrowRight, Sparkles, Camera, MapPin, PartyPopper, Cake } from "lucide-react";
import { getApiUrl } from "../utils/api";
import { Button } from "../components/ui/button";


export default function LandingPage() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: <PartyPopper className="w-8 h-8" />,
      title: "Site Personalizado",
      description: "Crie um site único para seu evento com nossa paleta exclusiva de cores e fontes elegantes."
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: "Lista de Presentes",
      description: "Monte sua lista de presentes e receba contribuições diretamente via PIX ou cartão."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Confirmação de Presença",
      description: "Gerencie os RSVPs dos seus convidados com formulário simples e exportação para CSV."
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Galeria de Fotos",
      description: "Compartilhe suas melhores fotos com seus convidados em uma galeria elegante."
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Localização",
      description: "Adicione o mapa com a localização do evento para facilitar a chegada dos convidados."
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Contagem Regressiva",
      description: "Mostre a contagem regressiva para o grande dia no seu site."
    }
  ];

  const eventTypes = [
    { name: "Casamentos", icon: "💍" },
    { name: "Aniversários", icon: "🎂" },
    { name: "Chá de Bebê", icon: "👶" },
    { name: "Formaturas", icon: "🎓" },
    { name: "Festas", icon: "🎉" },
    { name: "Corporativos", icon: "🏢" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#F3C9CF]/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PartyPopper className="w-8 h-8 text-[#B3121B]" />
            <span className="font-cursive text-3xl text-[#6E0F1A]">Fiesta Lab</span>
          </div>
          <Button
            data-testid="login-btn"
            onClick={handleLogin}
            className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-6 py-2 font-medium transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            Entrar
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen hero-gradient relative overflow-hidden pt-20">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="text-[#B3121B] font-medium uppercase tracking-widest text-sm mb-4 block">
                Seu evento digital personalizado
              </span>
              <h1 className="font-serif text-5xl md:text-7xl text-[#6E0F1A] leading-tight mb-6">
                Crie o site do seu
                <span className="font-cursive text-[#B3121B] block mt-2">evento</span>
              </h1>
              <p className="text-lg md:text-xl text-[#6E0F1A]/80 leading-relaxed mb-8 max-w-lg">
                Uma plataforma completa para organizar casamentos, aniversários, 
                chás de bebê, formaturas e qualquer celebração especial.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="get-started-btn"
                  onClick={handleLogin}
                  className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl flex items-center gap-2"
                >
                  Começar Agora
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="relative animate-slide-up stagger-2 hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Celebração"
                  className="rounded-3xl shadow-2xl object-cover w-full h-[500px]"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#567D50] rounded-full flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#6E0F1A]">+10.000</p>
                      <p className="text-sm text-[#6E0F1A]/60">Eventos criados</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 bg-white border-b border-[#F3C9CF]/30">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-[#6E0F1A]/60 mb-8 uppercase tracking-widest text-sm">
            Perfeito para qualquer ocasião
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {eventTypes.map((event, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-[#FAFAFA] px-6 py-3 rounded-full hover:bg-[#F3C9CF]/30 transition-colors cursor-pointer"
              >
                <span className="text-2xl">{event.icon}</span>
                <span className="text-[#6E0F1A] font-medium">{event.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-[#FAFAFA] relative">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#B3121B] font-medium uppercase tracking-widest text-sm mb-4 block">
              Funcionalidades
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-4">
              Tudo que você precisa
            </h2>
            <p className="text-lg text-[#6E0F1A]/70 max-w-2xl mx-auto">
              Nossa plataforma oferece todas as ferramentas para criar o site perfeito do seu evento.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-feature group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#F3C9CF] rounded-2xl flex items-center justify-center mb-6 text-[#B3121B] group-hover:bg-[#B3121B] group-hover:text-white transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-2xl text-[#6E0F1A] mb-3">{feature.title}</h3>
                <p className="text-[#6E0F1A]/70 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gift List Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1513885535751-8b9238bd345a?crop=entropy&cs=srgb&fm=jpg&q=85&w=500"
                alt="Presentes"
                className="rounded-3xl shadow-xl object-cover w-full h-[400px]"
              />
            </div>
            <div>
              <span className="text-[#B3121B] font-medium uppercase tracking-widest text-sm mb-4 block">
                Lista de Presentes
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-6">
                Receba presentes de forma <span className="font-cursive text-[#B3121B]">simples</span>
              </h2>
              <p className="text-lg text-[#6E0F1A]/70 mb-6 leading-relaxed">
                Crie sua lista de presentes e receba contribuições diretamente na sua conta. 
                Seus convidados podem pagar via PIX ou cartão de crédito em até 4x.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#567D50] rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#6E0F1A]">Pagamento via PIX e Cartão (até 4x)</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#567D50] rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#6E0F1A]">Taxa de apenas 3% por transação</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#567D50] rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#6E0F1A]">Dashboard completo de arrecadação</span>
                </li>
              </ul>
              <Button
                onClick={handleLogin}
                className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                Criar Minha Lista
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#6E0F1A] relative">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-cursive text-5xl md:text-7xl text-white mb-6">
            Comece a celebrar
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Crie seu site de evento em minutos e compartilhe com todos os seus convidados.
          </p>
          <Button
            data-testid="cta-btn"
            onClick={handleLogin}
            className="bg-white text-[#B3121B] hover:bg-[#F3C9CF] rounded-full px-10 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
          >
            Criar Meu Evento Grátis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-[#F3C9CF]/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <PartyPopper className="w-6 h-6 text-[#B3121B]" />
              <span className="font-cursive text-2xl text-[#6E0F1A]">Fiesta Lab</span>
            </div>
            <p className="text-sm text-[#6E0F1A]/60">
              © 2025 Fiesta Lab — Seu evento digital personalizado
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
